import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Rocket, Loader2, Copy, Check, Github, Pencil, History, RotateCcw, Globe, Trash2, Download, FolderUp, Wrench } from "lucide-react";
import { Logo } from "@/components/Brand";
import api, { API, authedFetch, formatApiErrorDetail } from "@/lib/api";
import ChatPanel from "@/components/builder/ChatPanel";
import PreviewPanel from "@/components/builder/PreviewPanel";
import CodePanel from "@/components/builder/CodePanel";
import ImportDialog from "@/components/builder/ImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Builder() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activePath, setActivePath] = useState(null);

  const [models, setModels] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [mode, setMode] = useState("auto");
  const [model, setModel] = useState("");
  const [prompt, setPrompt] = useState("");
  // Autopilot defaults on: build the whole app in one pass rather than
  // stopping to ask about things it could reasonably decide.
  const [autopilot, setAutopilot] = useState(
    () => localStorage.getItem("catalina_autopilot") !== "off",
  );

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  // Multi-pass builds emit no tokens while writing files — the bodies are large
  // and the editor reads them from the saved project, not the stream. Without
  // this the chat looks frozen for minutes and the stall warning fires on a run
  // that is working perfectly.
  const [buildProgress, setBuildProgress] = useState(null);
  // Files the app links but does not have — a pass that failed, or a stylesheet
  // the markup invented. Repairable without regenerating the whole app.
  const [missing, setMissing] = useState([]);
  const [repairing, setRepairing] = useState(false);

  /**
   * What the app did when the preview actually ran it.
   *
   * Everything else in this product judges generated code by asking a model to
   * look at it, which is the one judgement that cannot catch its own mistakes.
   * These already happened, in a real browser, to the real page.
   */
  const [runtimeErrors, setRuntimeErrors] = useState([]);
  const [fixing, setFixing] = useState(false);

  /**
   * A page, or a real Vite and React project that gets compiled.
   *
   * Chosen here rather than by the model. Getting it wrong costs a build, and
   * the person asking is the one who knows which they wanted.
   */
  const [target, setTarget] = useState(
    () => localStorage.getItem("catalina_target") || "site",
  );
  /** What the compiler produced. A site has none, and the preview uses source. */
  const [built, setBuilt] = useState([]);

  const onRuntimeError = useCallback((error) => {
    setRuntimeErrors((current) => {
      // A render loop throws the same error every frame. One copy is the
      // evidence; a hundred is a memory leak.
      const key = `${error.kind}|${error.resource || ""}|${(error.detail || "").slice(0, 200)}`;
      if (current.some((e) => e.key === key)) return current;
      return [...current, { ...error, key }].slice(0, 12);
    });
  }, []);
  const [currentModel, setCurrentModel] = useState(null);
  const [sessionCost, setSessionCost] = useState(0);
  const [costFlash, setCostFlash] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [hasKeys, setHasKeys] = useState(null);
  const [versions, setVersions] = useState([]);
  const abortRef = useRef(null);
  const [pendingRestore, setPendingRestore] = useState(null);
  const [domainOpen, setDomainOpen] = useState(false);
  const [domains, setDomains] = useState([]);
  const [cnameTarget, setCnameTarget] = useState(null);
  const [domainInput, setDomainInput] = useState("");
  const [domainBusy, setDomainBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const onStop = () => {
    // Tagged, because this is the only abort that means the run is really over.
    // Every other one is a connection giving up on a run that continues.
    abortRef.current?.abort("user");
    toast.message("Generation stopped");
  };

  const loadDomains = () => {
    api.get(`/projects/${projectId}/domains`).then(({ data }) => {
      setDomains(data.domains || []);
      setCnameTarget(data.cname_target);
    });
  };

  const openDomains = () => { setDomainOpen(true); loadDomains(); };

  const addDomain = async () => {
    const d = domainInput.trim();
    if (!d) return;
    setDomainBusy(true);
    try {
      await api.post(`/projects/${projectId}/domains`, { domain: d });
      setDomainInput("");
      toast.success("Domain attached — configure the DNS record shown below");
      loadDomains();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDomainBusy(false);
    }
  };

  const removeDomain = async (d) => {
    try {
      await api.delete(`/projects/${projectId}/domains/${d}`);
      toast.success("Domain removed");
      loadDomains();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const loadVersions = useCallback(() => {
    api.get(`/projects/${projectId}/versions`).then(({ data }) => setVersions(data));
  }, [projectId]);

  /**
   * Files the app points at and does not have.
   *
   * Asked of the server rather than waited for from the preview. A build that
   * lost a stylesheet is broken whether or not anyone has opened the preview to
   * see the red box, and an app broken weeks ago answers the same as one broken
   * a moment ago.
   */
  const loadMissing = useCallback(() => {
    api.get(`/projects/${projectId}/missing`)
      .then(({ data }) => setMissing(data.missing || []))
      // A diagnosis that fails is not worth a toast. The app still works, and
      // the banner simply does not appear.
      .catch(() => setMissing([]));
  }, [projectId]);

  const loadFiles = useCallback(() => {
    api.get(`/projects/${projectId}/files`).then(({ data }) => {
      setFiles(data);
      setActivePath((p) => p || data.find((f) => f.path.endsWith("index.html"))?.path || data[0]?.path || null);
      loadMissing();
      // The compiled output, when this project has one. A site answers
      // `built: false` and the preview falls back to the source.
      api.get(`/projects/${projectId}/build`)
        .then(({ data }) => setBuilt(data.built ? data.files : []))
        .catch(() => setBuilt([]));
      // Different files are a different app, so what the last one did on load
      // says nothing about this one. The preview re-runs and reports again.
      setRuntimeErrors([]);
    });
  }, [projectId, loadMissing]);

  /**
   * Sends the browser's own report back to be fixed.
   *
   * The alternative, and what people did instead, was to describe the symptom
   * into the chat — losing the stack, the file and the line, which is the only
   * part of it that identifies the cause.
   */
  const fixRuntimeErrors = async () => {
    if (fixing || runtimeErrors.length === 0) return;
    setFixing(true);
    const attempted = runtimeErrors;

    try {
      const res = await authedFetch(`${API}/projects/${projectId}/fix`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          errors: attempted.map(({ kind, detail, resource, page }) => ({ kind, detail, resource, page })),
        }),
      });
      if (!res.ok) {
        let detail = `Server error (${res.status})`;
        try { const j = await res.json(); detail = formatApiErrorDetail(j.detail) || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }
      if (!res.body) throw new Error("Your browser does not support streaming responses");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let summary = "";
      let changed = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }

          if (evt.type === "token") summary += evt.content;
          else if (evt.type === "files") changed = evt.count || 0;
          else if (evt.type === "cost") setSessionCost((s) => s + (evt.cost_usd || 0));
          else if (evt.type === "warning") toast.warning(evt.message);
          else if (evt.type === "error") throw new Error(evt.message);
        }
      }

      const said = summary.split("===FILE:")[0].trim();
      setMessages((m) => [...m, {
        role: "assistant",
        content: said || `Changed ${changed} file(s) from what the browser reported.`,
      }]);
      toast.success(changed > 0 ? `Fixed — ${changed} file(s) changed` : "Nothing needed changing");
    } catch (err) {
      toast.error(err.message || "The fix failed");
    } finally {
      setFixing(false);
      // Reloading clears the list; the preview re-runs and reports whatever is
      // still wrong, which is how the loop knows whether it worked.
      loadFiles();
      loadVersions();
    }
  };

  /**
   * Rebuilds only the files that are missing.
   *
   * The alternative was to type "you forgot styles.css" into the chat and pay
   * for a whole regeneration, which rewrites every page that was already right.
   */
  const repair = async () => {
    if (repairing || missing.length === 0) return;
    const writable = missing.filter((m) => m.writable);
    if (writable.length === 0) return;

    setRepairing(true);
    try {
      const res = await authedFetch(`${API}/projects/${projectId}/repair`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paths: writable.map((m) => m.path) }),
      });
      if (!res.ok) {
        let detail = `Server error (${res.status})`;
        try { const j = await res.json(); detail = formatApiErrorDetail(j.detail) || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }
      if (!res.body) throw new Error("Your browser does not support streaming responses");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let rebuilt = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }

          if (evt.type === "file_done") rebuilt.push(evt.path);
          else if (evt.type === "cost") setSessionCost((s) => s + (evt.cost_usd || 0));
          else if (evt.type === "warning") toast.warning(evt.message);
          else if (evt.type === "error") throw new Error(evt.message);
        }
      }

      if (rebuilt.length > 0) {
        toast.success(`Rebuilt ${rebuilt.join(", ")}`);
        setMessages((m) => [...m, {
          role: "assistant",
          content: `Rebuilt the files this app was missing: ${rebuilt.join(", ")}.`,
        }]);
      } else {
        toast.warning("Nothing could be rebuilt. Try again, or ask for it in the chat.");
      }
    } catch (err) {
      toast.error(err.message || "The repair failed");
    } finally {
      setRepairing(false);
      // Whatever happened, both lists are now out of date.
      loadFiles();
      loadVersions();
    }
  };

  useEffect(() => {
    api.get(`/projects/${projectId}`).then(({ data }) => setProject(data))
      .catch(() => { toast.error("Project not found"); navigate("/dashboard"); });
    api.get(`/projects/${projectId}/messages`).then(({ data }) => {
      setMessages(data);
      setSessionCost(data.reduce((s, m) => s + (m.cost_usd || 0), 0));
    });
    api.get("/models").then(({ data }) => {
      setModels(data.models);
      setAllowed(data.allowed);
      setModel(data.allowed[0] || data.models[0]?.id || "");
    });
    api.get("/keys").then(({ data }) => setHasKeys(data.length > 0));
    loadFiles();
    loadVersions();
  }, [projectId, loadFiles, loadVersions, navigate]);

  const pollForResult = useCallback((assistantBaseline) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const [{ data: msgs }, { data: fs }] = await Promise.all([
          api.get(`/projects/${projectId}/messages`),
          api.get(`/projects/${projectId}/files`),
        ]);
        // Only a NEW assistant message means this run finished. The old
        // condition also accepted `fs.length > 0`, which is true from the
        // second generation onwards — so every iteration reported success on
        // the first poll, four seconds in, showing the previous files.
        const assistantCount = msgs.filter((m) => m.role === "assistant").length;
        if (assistantCount > assistantBaseline) {
          setMessages(msgs);
          setSessionCost(msgs.reduce((s, m) => s + (m.cost_usd || 0), 0));
          setFiles(fs);
          setActivePath((p) => p || fs.find((f) => f.path.endsWith("index.html"))?.path || fs[0]?.path || null);
          loadVersions();
          clearInterval(iv);
          toast.success("App generated");
          return;
        }
      } catch { /* keep polling */ }

      if (tries >= 45) {
        clearInterval(iv);
        // Previously this gave up silently and the user waited forever.
        toast.error("Still no result after three minutes", {
          description: "The generation may still be running. Reload the page to check.",
        });
      }
    }, 4000);
  }, [projectId, loadVersions]);

  const onSend = async (text, attachments = []) => {
    setPrompt("");
    const assistantBaseline = messages.filter((m) => m.role === "assistant").length;
    setMessages((m) => [
      ...m,
      // `attachments` is kept alongside the count so Retry can send the same
      // turn again intact. It is in-session only; history reloaded from the
      // server carries the count and not the images.
      { role: "user", content: text, attachmentCount: attachments.length, attachments },
    ]);
    setIsStreaming(true);
    setStreamingText("");
    setBuildProgress(null);
    setCurrentModel(null);

    let acc = "";
    let gotTerminal = false;
    const controller = new AbortController();
    abortRef.current = controller;
    // Ten minutes, matching the Durable Object's own deadline. Five was fine
    // when a build was one provider call; a multi-pass build is one call per
    // file and routinely runs longer, so the client was giving up on runs the
    // server was still happily working on.
    //
    // The reason matters more than the duration. An abort here means the local
    // connection gave up, not that the run did — the object outlives it — so it
    // has to be told apart from the user pressing Stop, which is the one case
    // where nothing is coming.
    const timer = setTimeout(() => controller.abort("timeout"), 600000);
    try {
      const res = await authedFetch(`${API}/projects/${projectId}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: text,
          mode,
          model: mode === "manual" ? model : null,
          autopilot,
          target,
          attachments,
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        let detail = `Server error (${res.status})`;
        try { const j = await res.json(); detail = formatApiErrorDetail(j.detail) || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }
      if (!res.body) throw new Error("Your browser does not support streaming responses");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.type === "model") {
            setCurrentModel({ label: evt.label, mode: evt.mode, autopilot: evt.autopilot });
          } else if (evt.type === "question") {
            // Autopilot hit an ambiguity it could not resolve. Nothing was
            // built, so this ends the turn rather than waiting for files.
            gotTerminal = true;
            setMessages((m) => [...m, { role: "assistant", content: evt.message, isQuestion: true }]);
            setStreamingText("");
          } else if (evt.type === "token") {
            acc += evt.content;
            setStreamingText(acc);
          } else if (evt.type === "plan") {
            setBuildProgress({ planned: evt.files, done: [], current: null, collections: [] });
          } else if (evt.type === "collections") {
            setBuildProgress((p) => ({ ...(p || { planned: [], done: [] }), collections: evt.collections }));
          } else if (evt.type === "file_start") {
            setBuildProgress((p) => ({
              ...(p || { planned: [], done: [], collections: [] }),
              current: { path: evt.path, index: evt.index, total: evt.total },
            }));
          } else if (evt.type === "images") {
            setBuildProgress((p) => ({ ...(p || { planned: [], done: [], collections: [] }), imageCount: evt.count }));
          } else if (evt.type === "image_done") {
            setBuildProgress((p) => ({
              ...(p || { planned: [], done: [], collections: [] }),
              imagesDone: evt.done, imageCount: evt.total,
            }));
          } else if (evt.type === "compile") {
            setBuildProgress((p) => ({
              ...(p || { planned: [], done: [], collections: [] }),
              compiling: { attempt: evt.attempt, of: evt.of },
            }));
          } else if (evt.type === "compile_failed") {
            setBuildProgress((p) => ({
              ...(p || { planned: [], done: [], collections: [] }),
              compileError: { attempt: evt.attempt, stage: evt.stage, detail: evt.detail },
            }));
          } else if (evt.type === "compiled") {
            setBuildProgress((p) => ({
              ...(p || { planned: [], done: [], collections: [] }),
              compiling: null,
              compiled: { files: evt.files, ms: evt.ms },
            }));
          } else if (evt.type === "review_start") {
            setBuildProgress((p) => ({ ...(p || { planned: [], done: [], collections: [] }), reviewing: true }));
          } else if (evt.type === "review_done") {
            setBuildProgress((p) => ({
              ...(p || { planned: [], done: [], collections: [] }),
              reviewing: false, reviewChanged: evt.changed, reviewNote: evt.note,
            }));
          } else if (evt.type === "file_done") {
            setBuildProgress((p) => ({
              ...(p || { planned: [], collections: [] }),
              done: [...((p && p.done) || []), evt.path],
              current: null,
            }));
          } else if (evt.type === "cost") {
            gotTerminal = true;
            const c = evt.cost_usd || 0;
            setMessages((m) => [...m, {
              role: "assistant", content: acc, model_used: currentModel?.label,
              cost_usd: c, tokens_in: evt.tokens_in, tokens_out: evt.tokens_out,
            }]);
            setSessionCost((s) => s + c);
            setCostFlash(true);
            setTimeout(() => setCostFlash(false), 600);
            setStreamingText("");
          } else if (evt.type === "files") {
            loadFiles();
          } else if (evt.type === "warning") {
            toast.warning(evt.message);
          } else if (evt.type === "error") {
            gotTerminal = true;
            toast.error(evt.message);
            setMessages((m) => [...m, { role: "assistant", content: `⚠ ${evt.message}` }]);
            setStreamingText("");
          }
        }
      }
      // Stream ended without a terminal event — the generation is still finishing in
      // the background (platform caps the response at ~60s). Poll for the saved result.
      if (!gotTerminal) {
        if (acc.trim()) {
          setMessages((m) => [...m, { role: "assistant", content: acc }]);
          toast.message("Still generating — finishing in the background…", { description: "The files will appear in a few seconds." });
          pollForResult(assistantBaseline);
        } else {
          toast.message("Still generating — finishing in the background…", { description: "The files will appear in a few seconds." });
          pollForResult(assistantBaseline);
        }
      }
    } catch (err) {
      if (err.name === "AbortError" && controller.signal.reason === "user") {
        setMessages((m) => [...m, { role: "assistant", content: "⚠ Generation stopped." }]);
      } else {
        // A dropped connection, or this client timing out on a long build.
        // Either way the run continues in the Durable Object, so the result is
        // collected by polling rather than mourned.
        toast.message("Lost the live stream — collecting the result…", {
          description: "The generation is still running on the server. The files will appear in a few seconds.",
        });
        setMessages((m) => [...m, { role: "assistant", content: acc || "" }].filter((m) => m.content !== ""));
        pollForResult(assistantBaseline);
      }
    } finally {
      clearTimeout(timer);
      setIsStreaming(false);
      setStreamingText("");
      setCurrentModel(null);
      api.get(`/projects/${projectId}`).then(({ data }) => setProject(data));
      api.get("/keys").then(({ data }) => setHasKeys(data.length > 0)).catch(() => {});
      loadVersions();
    }
  };

  const restoreVersion = async (versionId) => {
    try {
      await api.post(`/projects/${projectId}/versions/${versionId}/restore`);
      loadFiles();
      toast.success("Version restored");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  /**
   * Starts the generation the Dashboard was asked for.
   *
   * The prompt arrives in router state rather than the URL: it can be long,
   * and a query string would put the user's brief in browser history and in
   * any referrer. The state is cleared immediately so reloading the builder
   * does not run the same generation a second time.
   */
  const autoStarted = useRef(false);
  useEffect(() => {
    const initial = location.state?.initialPrompt;
    if (!initial || autoStarted.current || hasKeys === null) return;

    // What the dashboard was set to when the brief was written. The first build
    // decides what the project is, so it has to honour the choice made at the
    // moment somebody described it rather than a default picked here.
    const chosen = location.state?.initialTarget;
    if (chosen === "app" || chosen === "site") setTarget(chosen);

    autoStarted.current = true;
    navigate(location.pathname, { replace: true, state: null });

    // With no provider key the run would only fail; leave it in the composer.
    if (hasKeys === false) {
      setPrompt(initial);
      return;
    }
    onSend(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, hasKeys]);

  const downloadZip = async () => {
    try {
      const res = await authedFetch(`${API}/projects/${projectId}/download`);
      if (!res.ok) {
        let d = `Error ${res.status}`;
        try { const j = await res.json(); d = formatApiErrorDetail(j.detail) || d; } catch { /* */ }
        throw new Error(d);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project?.name || "project").replace(/[^a-z0-9-_]/gi, "-")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      toast.error("Download failed: " + err.message);
    }
  };

  /**
   * @param force publish even though the app links files it does not have.
   *
   * The server refuses by default, and it is right to: a deploy is the one
   * irreversible step here — it puts a URL in front of somebody's customers.
   * A shop went live with five dead links in its navigation because nothing
   * asked before publishing.
   */
  const deploy = async (force = false) => {
    setDeploying(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/deploy`, { force });
      setProject((p) => ({ ...p, deploy_url: data.deploy_url, status: "deployed" }));
      toast.success("Deployed to " + data.deploy_url);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 409) {
        // Not a failure to report and forget — the remedy is one click away,
        // and taking it is almost always what the person wanted.
        toast.error(detail, {
          duration: 12000,
          action: { label: "Rebuild them", onClick: () => repair() },
          cancel: { label: "Publish anyway", onClick: () => deploy(true) },
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setDeploying(false);
    }
  };

  const copyDeploy = () => {
    navigator.clipboard.writeText(project.deploy_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const pushGithub = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/push`);
      setProject((p) => ({ ...p, github_repo_url: data.repo_url }));
      toast.success(`Pushed ${data.pushed}/${data.total} files to GitHub`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPushing(false);
    }
  };

  const onFileSaved = (path, content) => {
    setFiles((fs) => fs.map((f) => (f.path === path ? { ...f, content } : f)));
  };

  const startRename = () => {
    setNameDraft(project.name);
    setEditingName(true);
  };

  const saveName = async () => {
    const name = nameDraft.trim();
    setEditingName(false);
    if (!name || name === project.name) return;
    try {
      const { data } = await api.put(`/projects/${projectId}`, { name });
      setProject((p) => ({ ...p, name: data.name }));
      toast.success("Project renamed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const enrichedMessages = messages.map((m) =>
    m.role === "assistant" && !m.model_used && currentModel
      ? { ...m, model_used: currentModel.label } : m
  );

  if (!project) {
    return <div className="h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>;
  }

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Global header */}
      <header className="h-12 shrink-0 border-b border-border flex items-center justify-between px-4 backdrop-blur-xl bg-background/70">
        <div className="flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Logo />
          <span className="text-zinc-700">/</span>
          {editingName ? (
            <Input
              data-testid="project-name-edit"
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
              className="h-7 w-[220px] bg-card border-border font-app text-sm"
            />
          ) : (
            <button data-testid="project-name" onClick={startRename}
              title="Click to rename"
              className="group flex items-center gap-1.5 font-app text-sm font-medium truncate max-w-[240px] hover:text-white transition-colors">
              <span className="truncate">{project.name}</span>
              <Pencil className="w-3 h-3 text-zinc-600 group-hover:text-kinetic transition-colors shrink-0" />
            </button>
          )}
          <Badge variant="outline" className="font-mono text-[10px] uppercase border-border text-muted-foreground">{project.status}</Badge>
        </div>
        <div className="flex items-center gap-3">
          {/* Cost pill */}
          <div data-testid="cost-pill" className={`flex items-center gap-2 border border-border rounded-full px-3 py-1 bg-card ${costFlash ? "cost-flash" : ""}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-kinetic animate-pulse" />
            <span className="font-mono text-xs text-muted-foreground">session</span>
            <span className={`font-mono text-xs font-semibold ${costFlash ? "cost-flash" : "text-white"}`}>${sessionCost === 0 ? "0.00" : sessionCost.toFixed(5)}</span>
          </div>
          {project.github_repo_url && (
            <a data-testid="github-repo-link" href={project.github_repo_url} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 font-mono text-xs text-muted-foreground hover:text-white hover:border-zinc-600 transition-colors">
              <Github className="w-3.5 h-3.5" /> repo
            </a>
          )}
          {/* Version history */}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="history-btn" asChild>
              <Button variant="outline" size="sm" className="border-border h-8" disabled={versions.length === 0}
                title={versions.length === 0 ? "No versions yet" : "Version history"}>
                <History className="w-3.5 h-3.5 mr-1.5" /> History {versions.length > 0 && `(${versions.length})`}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover border-border w-80 max-h-96 overflow-y-auto">
              <DropdownMenuLabel className="font-mono text-xs text-muted-foreground">VERSIONS</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {versions.map((v) => (
                <DropdownMenuItem key={v.id} data-testid={`version-${v.id}`}
                  onClick={() => setPendingRestore(v.id)} className="cursor-pointer flex-col items-start gap-0.5 py-2">
                  <div className="flex items-center justify-between w-full">
                    <span className="font-mono text-xs text-kinetic">v{v.index} · {v.model}</span>
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><RotateCcw className="w-3 h-3" /> restore</span>
                  </div>
                  <span className="text-xs text-zinc-300 truncate w-full">{v.label}</span>
                  <span className="font-mono text-[10px] text-zinc-600">{v.file_count} files · ${v.cost_usd.toFixed(5)}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="import-btn" onClick={() => setImportOpen(true)}
            size="sm" variant="outline" className="border-border h-8" title="Import files or a GitHub repository">
            <FolderUp className="w-3.5 h-3.5" />
          </Button>
          <Button data-testid="download-zip-btn" onClick={downloadZip} disabled={files.length === 0}
            size="sm" variant="outline" className="border-border h-8" title={files.length === 0 ? "Generate an app first" : "Download as .zip"}>
            <Download className="w-3.5 h-3.5" />
          </Button>
          <Button data-testid="push-github-btn" onClick={pushGithub} disabled={pushing || files.length === 0}
            title={files.length === 0 ? "Generate an app first" : "Push files to GitHub"}
            size="sm" variant="outline" className="border-border h-8">
            {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Github className="w-3.5 h-3.5 mr-1.5" /> {project.github_repo_url ? "Re-push" : "Push"}</>}
          </Button>
          {/* The live URL and its domain manager, once there is a deployment. */}
          {project.deploy_url && (
            <>
              <button data-testid="domain-btn" onClick={openDomains} title="Custom domain"
                className="flex items-center gap-1.5 border border-border rounded-md px-2.5 py-1.5 text-muted-foreground hover:text-white hover:border-zinc-600 transition-colors">
                <Globe className="w-3.5 h-3.5" />
              </button>
              <button data-testid="copy-deploy-btn" onClick={copyDeploy} title={project.deploy_url}
                className="flex items-center gap-2 border border-green-500/40 text-green-400 rounded-md px-3 py-1.5 font-mono text-xs hover:bg-green-500/10 transition-colors">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span className="max-w-[160px] truncate">{project.deploy_url.replace("https://", "")}</span>
              </button>
            </>
          )}

          {/*
            Always rendered. This used to be the else-branch of the block
            above, so the moment a project was deployed the button vanished
            and there was no way to publish any later change — the live site
            silently stayed on the first version forever.
          */}
          <Button data-testid="deploy-btn" onClick={deploy} disabled={deploying || files.length === 0}
            title={files.length === 0 ? "Generate an app first" : project.deploy_url ? "Publish the current files" : "Deploy to Cloudflare Pages"}
            size="sm" className="bg-white text-black hover:bg-zinc-200 h-8">
            {deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <><Rocket className="w-3.5 h-3.5 mr-1.5" /> {project.deploy_url ? "Redeploy" : "Deploy"}</>
            )}
          </Button>
        </div>
      </header>

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={projectId}
        onImported={() => { loadFiles(); loadVersions(); }}
      />

      {/* Restore confirmation */}
      <AlertDialog open={!!pendingRestore} onOpenChange={(o) => !o && setPendingRestore(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-app">Restore this version?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This will replace the current files with the selected version. Unsaved edits in the current files will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border bg-transparent">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-restore" className="bg-kinetic hover:bg-kinetic/90 text-white"
              onClick={() => { const id = pendingRestore; setPendingRestore(null); restoreVersion(id); }}>
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Custom domain manager */}
      <Dialog open={domainOpen} onOpenChange={setDomainOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-app flex items-center gap-2"><Globe className="w-4 h-4 text-kinetic" /> Custom domain</DialogTitle>
            <DialogDescription className="text-muted-foreground">Point your own domain to this Cloudflare Pages app.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="flex items-center gap-2">
              <Input data-testid="domain-input" value={domainInput} onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addDomain(); }}
                placeholder="app.tudominio.com" className="bg-background border-border font-mono text-sm" />
              <Button data-testid="add-domain-btn" onClick={addDomain} disabled={domainBusy}
                className="bg-white text-black hover:bg-zinc-200">
                {domainBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Attach"}
              </Button>
            </div>
            {cnameTarget && (
              <div className="border border-border rounded-lg p-3 bg-background">
                <p className="text-xs text-muted-foreground mb-1">Add this DNS record at your registrar:</p>
                <code className="font-mono text-xs text-zinc-300">CNAME &nbsp;→&nbsp; <span className="text-kinetic">{cnameTarget}</span></code>
              </div>
            )}
            <div className="space-y-2">
              {domains.length === 0 ? (
                <p className="text-xs text-zinc-600 font-mono">No custom domains attached yet.</p>
              ) : domains.map((d) => (
                <div key={d.name} data-testid={`domain-row-${d.name}`} className="flex items-center justify-between border border-border rounded-lg px-3 py-2 bg-background">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-200">{d.name}</span>
                    <Badge variant="outline" className="font-mono text-[10px] border-border text-muted-foreground">{d.status || "pending"}</Badge>
                  </div>
                  <button data-testid={`remove-domain-${d.name}`} onClick={() => removeDomain(d.name)}
                    className="text-zinc-600 hover:text-kinetic transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <a href={project.deploy_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-white transition-colors font-mono">
              current: {project.deploy_url?.replace("https://", "")}
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        Files the app links and does not have.
        Above the panels rather than inside the preview, because it is a fact
        about the project rather than about the frame — and because the remedy
        used to be "describe the problem in the chat and pay for a whole
        regeneration", which rewrites every page that was already right.
      */}
      {missing.length > 0 && !isStreaming && (
        <div
          data-testid="missing-files"
          className="border-b border-amber-500/40 bg-amber-500/10 px-4 py-2.5 flex items-center gap-3 flex-wrap"
        >
          <span className="font-app text-xs font-semibold text-amber-300">
            {missing.length === 1 ? "1 file is missing" : `${missing.length} files are missing`}
          </span>
          <span className="font-mono text-xs text-amber-200/70">
            {missing.map((m) => m.path).join(", ")}
          </span>
          {missing.some((m) => m.writable) ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 ml-auto"
              onClick={repair}
              disabled={repairing}
            >
              {repairing ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Rebuilding…</>
              ) : (
                <><RotateCcw className="w-3 h-3 mr-1.5" /> Rebuild missing files</>
              )}
            </Button>
          ) : (
            // Images and other binaries are just as broken, and nothing a text
            // pass writes would be a photograph. Saying so beats a button that
            // produces a JPEG full of prose.
            <span className="ml-auto text-xs text-amber-200/70">
              These cannot be generated as text — upload them, or ask for the
              references to be removed.
            </span>
          )}
        </div>
      )}

      {/*
        What the app did when it was RUN.
        Separate from the missing-files banner above on purpose: that one is a
        fact about the project's files, this one is a fact about its behaviour,
        and only one of them can be established without loading the page.
      */}
      {runtimeErrors.length > 0 && !isStreaming && (
        <div
          data-testid="runtime-errors"
          className="border-b border-kinetic/40 bg-kinetic/10 px-4 py-2.5"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-app text-xs font-semibold text-red-300">
              {runtimeErrors.length === 1
                ? "This app threw an error when it ran"
                : `This app threw ${runtimeErrors.length} errors when it ran`}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 ml-auto"
              onClick={fixRuntimeErrors}
              disabled={fixing}
            >
              {fixing ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Fixing…</>
              ) : (
                <><Wrench className="w-3 h-3 mr-1.5" /> Fix from the error</>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-zinc-500"
              onClick={() => setRuntimeErrors([])}
              disabled={fixing}
            >
              Dismiss
            </Button>
          </div>
          <ul className="mt-1.5 space-y-0.5">
            {runtimeErrors.slice(0, 4).map((e) => (
              <li key={e.key} className="font-mono text-xs text-red-200/70 truncate">
                {e.kind}
                {e.resource ? ` · ${e.resource}` : ""}
                {e.detail ? ` · ${e.detail.split("\n")[0]}` : ""}
              </li>
            ))}
            {runtimeErrors.length > 4 && (
              <li className="font-mono text-xs text-zinc-600">
                and {runtimeErrors.length - 4} more
              </li>
            )}
          </ul>
        </div>
      )}

      {/* 3-panel IDE */}
      <div className="flex-1 grid grid-cols-12 min-h-0">
        <div className="col-span-3 border-r border-border min-h-0">
          <ChatPanel
            messages={enrichedMessages} streamingText={streamingText} isStreaming={isStreaming}
            buildProgress={buildProgress}
            currentModel={currentModel} onSend={onSend} onStop={onStop} models={models} allowed={allowed}
            mode={mode} setMode={setMode} model={model} setModel={setModel}
            prompt={prompt} setPrompt={setPrompt} hasKeys={hasKeys}
            autopilot={autopilot}
            setAutopilot={(value) => {
              setAutopilot(value);
              localStorage.setItem("catalina_autopilot", value ? "on" : "off");
            }}
            target={target}
            setTarget={(value) => {
              setTarget(value);
              localStorage.setItem("catalina_target", value);
            }}
          />
        </div>
        <div className="col-span-5 border-r border-border min-h-0">
          <PreviewPanel files={built.length > 0 ? built : files} deployUrl={project.deploy_url} onRuntimeError={onRuntimeError} />
        </div>
        <div className="col-span-4 min-h-0">
          <CodePanel projectId={projectId} files={files} activePath={activePath}
            setActivePath={setActivePath} onFileSaved={onFileSaved} />
        </div>
      </div>
    </div>
  );
}
